import { useEffect, useState } from "react";
import { authHeaders } from "../api/auth";

type Consent = { applies: boolean; granted: boolean; version: string; text: string };

export default function PrivacyConsentPanel() {
  const [consent, setConsent] = useState<Consent | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    fetch("/memberships/privacy-consent", { headers: authHeaders(), cache: "no-store" })
      .then(async response => {
        if (!response.ok) throw new Error("Could not load privacy controls. Please refresh or contact hello@calmbutcapable.com.");
        return response.json();
      })
      .then(data => { if (active) setConsent(data); })
      .catch(reason => { if (active) setError(reason.message); });
    return () => { active = false; };
  }, []);

  async function save(granted: boolean) {
    if (!consent || busy || (granted && !checked)) return;
    setBusy(true); setError("");
    try {
      const response = await fetch("/memberships/privacy-consent", {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ granted, version: consent.version }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not save your privacy choice.");
      setConsent(data); setChecked(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save your privacy choice.");
    } finally { setBusy(false); }
  }

  if (!consent) return error ? <p role="alert" style={{ padding: 16 }}>{error}</p> : null;
  if (!consent.applies) return null;
  return <section aria-label="Privacy controls" style={{ background: "white", color: "#172b3d", padding: 20, borderBottom: "1px solid #ccc", lineHeight: 1.5 }}>
    <details open={!consent.granted}>
      <summary><strong>Privacy controls</strong> — {consent.granted ? "Personal workspace consent given" : "Personal workspace processing paused"}</summary>
      <p>CBC does not sell your personal information or share journals or conversations for third-party marketing. Hosting and AI providers process information where needed to provide Sanctuary.</p>
      <p>{consent.text}</p>
      {consent.granted ? <button type="button" disabled={busy} onClick={() => save(false)}>Withdraw personal workspace consent</button> : <>
        <p>New entries, AI requests and personal summaries are paused. You can still manage your account, read and export saved entries, and request deletion. Billing continues unless you cancel it. Contact hello@calmbutcapable.com for cancellation, account closure or a review of retained information. Withdrawal does not recall an AI request already sent.</p>
        <label><input type="checkbox" checked={checked} disabled={busy} onChange={event => setChecked(event.target.checked)} /> I explicitly give the consent stated above.</label>
        <p><button type="button" disabled={busy || !checked} onClick={() => save(true)}>Save my consent</button></p>
      </>}
      {error && <p role="alert">{error}</p>}
      {!error && consent.granted && <p role="status">Your consent is recorded. <button type="button" onClick={() => window.location.reload()}>Refresh Sanctuary</button> to reopen any paused features.</p>}
    </details>
  </section>;
}
